"use client";

import { useEffect, useState } from "react";
import { apiGet, apiPut } from "@/integrations/api/client";
import type { RouteKey } from "@/lib/routes/routes";

type Json = string | number | boolean | null | { [key: string]: Json | undefined } | Json[];

export type ManagedPageKey =
  | "about"
  | "services"
  | "mvp"
  | "projects"
  | "blog"
  | "method"
  | "subscription"
  | "contacts";

export type PageVisibilityMap = Record<ManagedPageKey, boolean>;

const STORAGE_KEY = "page_visibility";

const DEFAULT_VISIBILITY: PageVisibilityMap = {
  about: true,
  services: true,
  mvp: true,
  projects: true,
  blog: true,
  method: true,
  subscription: true,
  contacts: true,
};

const MANAGED_KEYS = Object.keys(DEFAULT_VISIBILITY) as ManagedPageKey[];
let cachedVisibility: PageVisibilityMap = { ...DEFAULT_VISIBILITY };
let hasResolvedVisibility = false;
let inflightVisibilityRequest: Promise<PageVisibilityMap> | null = null;

function readStoredVisibility(): PageVisibilityMap {
  if (typeof window === "undefined") return { ...DEFAULT_VISIBILITY };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_VISIBILITY };
    const parsed = JSON.parse(raw) as Partial<PageVisibilityMap>;
    return { ...DEFAULT_VISIBILITY, ...parsed };
  } catch {
    return { ...DEFAULT_VISIBILITY };
  }
}

function storeVisibility(value: PageVisibilityMap) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
}

function parseVisibility(value: Json | null | undefined): PageVisibilityMap {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return { ...DEFAULT_VISIBILITY };
  }

  const input = value as Record<string, unknown>;
  const next = { ...DEFAULT_VISIBILITY };

  for (const key of MANAGED_KEYS) {
    if (typeof input[key] === "boolean") {
      next[key] = input[key] as boolean;
    }
  }

  return next;
}

export function usePageVisibility(options?: { defer?: boolean }) {
  // Always start with DEFAULT_VISIBILITY for server-client parity.
  // localStorage value resolved in mount effect (avoids hydration mismatch).
  const [visibility, setVisibility] = useState<PageVisibilityMap>(() => ({
    ...DEFAULT_VISIBILITY,
  }));
  const [loading, setLoading] = useState(!hasResolvedVisibility);
  const shouldDefer = options?.defer ?? false;

  const fetchVisibility = async () => {
    if (hasResolvedVisibility) {
      setVisibility(cachedVisibility);
      setLoading(false);
      return cachedVisibility;
    }

    if (!inflightVisibilityRequest) {
      inflightVisibilityRequest = (async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const data = await apiGet<{ value: Json }>("/settings/page_visibility");
            if (data?.value) {
              cachedVisibility = parseVisibility(data.value);
              storeVisibility(cachedVisibility);
            }
            hasResolvedVisibility = true;
            return cachedVisibility;
          } catch {
            if (attempt < 2) {
              await new Promise<void>((r) => setTimeout(r, 2000 * (attempt + 1)));
            }
          }
        }
        return cachedVisibility;
      })().finally(() => {
        inflightVisibilityRequest = null;
      });
    }

    setLoading(true);
    const nextVisibility = await inflightVisibilityRequest;
    setVisibility(nextVisibility);
    setLoading(false);
    return nextVisibility;
  };

  const updateVisibility = async (next: PageVisibilityMap) => {
    try {
      await apiPut("/admin/settings", {
        key: "page_visibility",
        value: next,
      });

      setVisibility(next);
      cachedVisibility = next;
      hasResolvedVisibility = true;
      storeVisibility(next);
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const setPageVisibility = async (key: ManagedPageKey, enabled: boolean) => {
    const next = { ...visibility, [key]: enabled };
    return updateVisibility(next);
  };

  const isVisible = (key: RouteKey) => {
    if (!MANAGED_KEYS.includes(key as ManagedPageKey)) return true;
    return visibility[key as ManagedPageKey] ?? DEFAULT_VISIBILITY[key as ManagedPageKey] ?? true;
  };

  useEffect(() => {
    cachedVisibility = readStoredVisibility();
    setVisibility(cachedVisibility);
    if (!shouldDefer || hasResolvedVisibility) {
      void fetchVisibility();
      return;
    }

    let cancelled = false;
    let timeoutId: number | null = null;
    let cleanupInteractions: (() => void) | null = null;

    const runFetch = () => {
      if (!cancelled) {
        void fetchVisibility();
      }
    };

    const interactionEvents: Array<keyof WindowEventMap> = [
      "scroll",
      "touchstart",
      "pointerdown",
      "keydown",
    ];
    const handleInteraction = () => {
      runFetch();
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction)
      );
    };
    interactionEvents.forEach((eventName) =>
      window.addEventListener(eventName, handleInteraction, {
        passive: true,
        once: true,
      })
    );
    cleanupInteractions = () => {
      interactionEvents.forEach((eventName) =>
        window.removeEventListener(eventName, handleInteraction)
      );
    };

    timeoutId = window.setTimeout(runFetch, 12000);

    return () => {
      cancelled = true;
      if (timeoutId !== null) {
        window.clearTimeout(timeoutId);
      }
      cleanupInteractions?.();
    };
  }, [shouldDefer]);

  const forceRefetch = async () => {
    hasResolvedVisibility = false;
    inflightVisibilityRequest = null;
    return fetchVisibility();
  };

  return {
    visibility,
    loading,
    setPageVisibility,
    isVisible,
    refetch: fetchVisibility,
    forceRefetch,
  };
}
